// public/v3/src/lib/payer-rules.js — payer-class ↔ program-code compatibility.
//
// One shared answer to "can this payer class be billed this code", consumed by
// push eligibility (client + server push route), the bulk-update guard, the
// Onboard candidacy predicates, and the APCM decision blockers. CommonJS so
// server routes can require it across the public/ boundary (the
// eligibility-infer pattern).
//
// Blockers are deliberately scoped to combinations KNOWN to be wrong:
// - NY Medicaid pays nothing for the CCM CPT family, 99484, or the
//   APCM/BHI/PCM-adjacent G-codes — its care-management path is CoCM (CCMP),
//   so the CoCM CPTs (99492/99493/99494) are deliberately NOT blocked here.
// - Commercial plans reimburse the CCM CPT family (verified: Anthem BCBS NY,
//   UHC) but not the Medicare-only G-code families.
// - 'unknown' (CSV/manual imports with no insurances on file) stays
//   permissive: classification, not absence of data, drives blocking —
//   matching the legacy posture for non-Elation patients.

const MEDICARE_ONLY_G_CODES = new Set(['G0556', 'G0557', 'G0558', 'G0570', 'G0568', 'G0569', 'G2214']);
const CCM_CPT_FAMILY = new Set(['99490', '99439', '99491', '99437', '99487', '99489']);
const BHI_MEDICARE_CPTS = new Set(['99484']);
// PCM: Medicare FFS + MA; absent from the eMedNY schedule (NY Medicaid does
// not pay it); commercial is contract-specific — left permissive like CCM.
const PCM_CPT_FAMILY = new Set(['99424', '99425', '99426', '99427']);

const MEDICARE_CLASSES = new Set(['medicare', 'ma', 'dual']);

function _cls(payerClass) {
  return String(payerClass || '').toLowerCase();
}

// True when the payer class supports the Medicare care-management code
// universe the app bills today ('' / unknown stay permissive).
function payerSupportsMedicarePrograms(payerClass) {
  const cls = _cls(payerClass);
  return !cls || cls === 'unknown' || MEDICARE_CLASSES.has(cls);
}

// Human-readable blocker when the code is known-unbillable for the payer
// class; null when allowed (or unknown/unclassified — permissive).
function payerProgramBlocker(payerClass, code) {
  const cls = _cls(payerClass);
  const c = String(code || '').toUpperCase();
  if (!c || !cls || cls === 'unknown' || MEDICARE_CLASSES.has(cls)) return null;
  if (cls === 'medicaid') {
    if (MEDICARE_ONLY_G_CODES.has(c) || CCM_CPT_FAMILY.has(c) || BHI_MEDICARE_CPTS.has(c) || PCM_CPT_FAMILY.has(c)) {
      return 'NY Medicaid does not reimburse ' + c + ' (Medicare-only care-management code)';
    }
    return null;
  }
  if (cls === 'commercial') {
    if (MEDICARE_ONLY_G_CODES.has(c)) {
      return c + ' is a Medicare-only G-code (commercial plans do not reimburse it)';
    }
    return null;
  }
  return null;
}

// First blocker across a bill's CPT lines (base + add-ons), or null.
function payerCptsBlocker(payerClass, cpts) {
  for (const c of Array.isArray(cpts) ? cpts : []) {
    const code = c && typeof c === 'object' ? (c.cptCode || c.cpt) : c;
    const blk = payerProgramBlocker(payerClass, code);
    if (blk) return blk;
  }
  return null;
}

// ── Sync allowlist resolution (client mirror) ──────────────────────────────
// Same semantics as the server's _getSyncPayerFilter, but over an
// already-parsed settings object (ccmApi.getSettings). Returns
// { classes: string[], admission: {cls:{tags:[]}} } or null when the filter
// is disabled (all payers admitted). Default = the legacy Medicare behavior.
const DEFAULT_ALLOWLIST_CLASSES = ['medicare', 'ma', 'dual'];
const PAYER_CLASS_VALUES = ['medicare', 'ma', 'dual', 'medicaid', 'commercial', 'unknown'];

function _isDisabledValue(v) {
  return v === false || v === 'false' || (v && typeof v === 'object' && v.enabled === false);
}

function resolvePayerAllowlistFromSettings(settings) {
  const s = settings || {};
  let classes;
  const allow = s.sync_payer_allowlist;
  if (allow !== undefined && allow !== null) {
    if (_isDisabledValue(allow)) return null;
    const list = allow && Array.isArray(allow.classes)
      ? allow.classes.map((c) => String(c).toLowerCase().trim()).filter(Boolean)
      : [];
    classes = list.length ? list : DEFAULT_ALLOWLIST_CLASSES.slice();
  } else if (_isDisabledValue(s.sync_medicare_filter)) {
    return null;
  } else {
    classes = DEFAULT_ALLOWLIST_CLASSES.slice();
  }
  const admission = {};
  const adm = s.sync_payer_admission;
  if (adm && typeof adm === 'object') {
    for (const k of Object.keys(adm)) {
      const tags = adm[k] && Array.isArray(adm[k].tags)
        ? adm[k].tags.map((t) => String(t).toLowerCase().trim()).filter(Boolean)
        : [];
      if (tags.length) admission[String(k).toLowerCase()] = { tags };
    }
  }
  return { classes, admission };
}

module.exports = {
  MEDICARE_ONLY_G_CODES,
  CCM_CPT_FAMILY,
  DEFAULT_ALLOWLIST_CLASSES,
  PAYER_CLASS_VALUES,
  payerSupportsMedicarePrograms,
  payerProgramBlocker,
  payerCptsBlocker,
  resolvePayerAllowlistFromSettings,
  MEDICARE_CLASSES,
};
